// Stacking order of the items placed in one section (docs/bands-spec.md
// §8.7). Once the user arranges a section, every item in it has a `layer`
// (1 = bottom) and the PDF draws them in that order; until then the PDF
// draws shapes first, then images, QR codes and the logo, then text.

const RANK = { shapes: 0, images: 1, qrCodes: 1, logo: 1 };

// The order (bottom first) of a section's items:
//   items  [{ id, path, layer?, zIndex? }] in canvas (DOM) order.
// - When any item has a layer, items go by layer; items without one (added
//   after the section was arranged) go on top, in canvas order.
// - Otherwise the order is the PDF's: shapes, then images, QR codes and the
//   logo, then text; within those by zIndex, then canvas order.
// Returns the ids.
export function getStackOrder(items) {
  const list = (items || []).map((item, index) => ({ ...item, index }));
  const layered = list.some((item) => Number.isFinite(item.layer));
  const key = layered
    ? (item) => [Number.isFinite(item.layer) ? item.layer : Infinity, item.index]
    : (item) => [RANK[item.path] ?? 2, Number(item.zIndex) || 0, item.index];
  return list
    .sort((a, b) => {
      const ka = key(a);
      const kb = key(b);
      for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return ka[i] < kb[i] ? -1 : 1;
      return 0;
    })
    .map((item) => item.id);
}

export const ARRANGE = { FRONT: "front", FORWARD: "forward", BACKWARD: "backward", BACK: "back" };

// The order after moving `selected` (ids) as one block:
// - front / back: to the top / bottom;
// - forward / backward: just past the nearest item above / below the block
//   that overlaps one of them (`overlaps(a, b)`), so each step shows.
// Returns the new order, or null when nothing would change.
export function arrangeOrder(order, selected, action, overlaps = () => true) {
  const chosen = new Set(selected);
  const picked = order.filter((id) => chosen.has(id));
  const rest = order.filter((id) => !chosen.has(id));
  if (picked.length === 0 || rest.length === 0) return null;

  let next;
  if (action === ARRANGE.FRONT) next = [...rest, ...picked];
  else if (action === ARRANGE.BACK) next = [...picked, ...rest];
  else {
    const indexes = picked.map((id) => order.indexOf(id));
    const touches = (id) => picked.some((p) => overlaps(p, id));
    if (action === ARRANGE.FORWARD) {
      const top = Math.max(...indexes);
      const target = order.slice(top + 1).find((id) => !chosen.has(id) && touches(id));
      if (!target) return null;
      const at = rest.indexOf(target) + 1;
      next = [...rest.slice(0, at), ...picked, ...rest.slice(at)];
    } else if (action === ARRANGE.BACKWARD) {
      const bottom = Math.min(...indexes);
      const target = order.slice(0, bottom).reverse().find((id) => !chosen.has(id) && touches(id));
      if (!target) return null;
      const at = rest.indexOf(target);
      next = [...rest.slice(0, at), ...picked, ...rest.slice(at)];
    } else return null;
  }
  return next.every((id, i) => id === order[i]) ? null : next;
}
